import { useEffect, useState } from 'react';
import {
  subscribeToPushPermissionModal,
  closePushPermissionModal,
  markPwaPromptShown,
} from '../../services/pushPermissionModalService';
import { isIOS, isStandalonePWA, hasWebPushSupport } from '../../services/platformService';
import { requestPushPermission } from '../../services/pushService';
import { whoAmI } from '../../services/authService';
import { logDebug } from '../../services/loggerService';
import { Modal } from '../feedback/Modal';
import { Button } from '../actions/Button';

/**
 * Push-permission prompt, ported from core with admin copy. Two variants
 * picked at render time from platform detection:
 *
 *   - iOS Safari tab (not installed): "Add to Home Screen" steps — iOS has no
 *     notification API outside an installed PWA.
 *   - Everything else: a short pitch and an Enable button, whose click
 *     satisfies the browser's user-gesture requirement.
 *
 * Opened from the first-launch trigger (installed iOS PWA) or the Account
 * page. `requestPushPermission()` resolves true only once permission, FCM
 * token and the device PUT have all succeeded.
 */
export function PushPermissionModal() {
  const [state, setState] = useState<{
    isOpen: boolean;
    fromFirstLaunch: boolean;
    onGranted?: () => void | Promise<void>;
  }>(() => ({ isOpen: false, fromFirstLaunch: false }));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => subscribeToPushPermissionModal(setState), []);

  const close = () => {
    if (state.fromFirstLaunch) markPwaPromptShown();
    closePushPermissionModal();
  };

  const enable = async () => {
    setSubmitting(true);
    try {
      const granted = await requestPushPermission();
      logDebug('Push permission modal result', undefined, { granted });
      if (granted) {
        try {
          await state.onGranted?.();
        } catch (err) {
          logDebug('Push onGranted callback threw', undefined, { error: String(err) });
        }
        try {
          // The first token PUT flips the profile's push preference on the BE; refresh to show it.
          await whoAmI();
        } catch {
          /* non-fatal: BE state is correct either way */
        }
      }
    } finally {
      setSubmitting(false);
      close();
    }
  };

  const install = (isIOS() && !isStandalonePWA()) || !hasWebPushSupport();

  if (install) {
    return (
      <Modal
        isOpen={state.isOpen}
        onClose={close}
        size="md"
        title="Add Mission Control to your Home Screen"
        footer={
          <Button size="sm" onClick={close}>
            Got it
          </Button>
        }
      >
        <p className="m-0 mb-3 text-sm text-muted">
          Alerts reach an iPhone only through the installed app. Adding it takes a moment:
        </p>
        <ol className="m-0 list-inside list-decimal space-y-2 pl-0 text-sm text-muted">
          <li>
            Tap <strong className="text-ink">Share</strong> in Safari.
          </li>
          <li>
            Tap <strong className="text-ink">Add to Home Screen</strong>, then <strong className="text-ink">Add</strong>
            .
          </li>
          <li>Open Mission Control from your Home Screen and allow notifications when asked.</li>
        </ol>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={state.isOpen}
      onClose={close}
      size="md"
      title="Turn on alert notifications"
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={close} disabled={submitting}>
            Not now
          </Button>
          <Button size="sm" onClick={enable} loading={submitting}>
            {submitting ? 'Asking…' : 'Enable notifications'}
          </Button>
        </>
      }
    >
      <p className="m-0 text-sm text-muted">
        Get a notification on this device when an alert that names you fires — a reconciliation break, a filing due
        date, a new home rule city. Only alerts, nothing else.
      </p>
    </Modal>
  );
}
